import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Center,
    Container,
    Group,
    Loader,
    Tabs,
    Text,
} from '@mantine/core';
import { useQuery } from '@apollo/client';

import { DashboardHeader, When } from '../../../components/common';
import ConfirmDeleteModal from '../../../components/common/ConfirmDeleteModal';
import {
    TrainingCategoryMaterialsPanel,
    TrainingMaterialEditModal,
    TrainingMaterialViewer,
} from '../../../components/training';
import {
    GET_TRAINING_CATEGORIES,
    GET_TRAINING_MATERIALS,
} from '../../../apollo/queries/training';
import { useTrainingMaterialActions } from '../../../hooks/useTrainingMaterialActions';
import { useUser } from '../../../hooks/useUser';
import { TrainingCategory, TrainingMaterial } from '../../../types/training';

function TrainingMaterialsPage() {
    const { isSuperAdmin } = useUser();
    const [activeCategoryId, setActiveCategoryId] = useState<string | null>(
        null,
    );
    const [searchByCategoryId, setSearchByCategoryId] = useState<
        Record<number, string>
    >({});

    const { data: categoriesData, loading: categoriesLoading } = useQuery(
        GET_TRAINING_CATEGORIES,
    );
    const { data: materialsData, loading: materialsLoading } = useQuery(
        GET_TRAINING_MATERIALS,
    );

    const categories: TrainingCategory[] =
        categoriesData?.trainingCategories ?? [];
    const materials: TrainingMaterial[] =
        materialsData?.trainingMaterials ?? [];

    const {
        viewerMaterial,
        viewUrl,
        viewLoading,
        editTarget,
        deleteTarget,
        actionLoadingId,
        updating,
        deleting,
        setEditTarget,
        setDeleteTarget,
        handleViewMaterial,
        handleDownloadMaterial,
        handleDownloadViewerMaterial,
        handleCloseViewer,
        handleCloseEdit,
        handleCloseDelete,
        handleSaveEdit,
        handleDelete,
    } = useTrainingMaterialActions({
        activeCategoryId,
        onActiveCategoryChange: setActiveCategoryId,
    });

    useEffect(() => {
        if (categories.length === 0) {
            setActiveCategoryId(null);
            return;
        }
        const stillValid = categories.some(
            category => String(category.id) === activeCategoryId,
        );
        if (!stillValid) {
            setActiveCategoryId(String(categories[0].id));
        }
    }, [categories, activeCategoryId]);

    const materialsByCategory = useMemo(() => {
        return materials.reduce((map, material) => {
            const list = map.get(material.categoryId) ?? [];
            list.push(material);
            map.set(material.categoryId, list);
            return map;
        }, new Map<number, TrainingMaterial[]>());
    }, [materials]);

    const handleCategorySearchChange =
        (categoryId: number) => (event: ChangeEvent<HTMLInputElement>) => {
            const nextValue = event.target.value;
            setSearchByCategoryId(current => ({
                ...current,
                [categoryId]: nextValue,
            }));
        };

    const loading = categoriesLoading || materialsLoading;

    return (
        <div className="relative pb-10">
            <DashboardHeader
                title="Training materials"
                hasBreadcrumb={false}
                hasDate
            />
            <div className="px-11 w-full mb-8">
                <When
                    condition={!loading}
                    fallback={
                        <Center className="py-16">
                            <Loader />
                        </Center>
                    }
                >
                    <When
                        condition={categories.length > 0}
                        fallback={
                            <Container
                                size="xl"
                                className="rounded-lg p-8 bg-white"
                            >
                                <Text fw={500} className="mb-1">
                                    No categories yet
                                </Text>
                                <Text size="sm" c="dimmed">
                                    <When
                                        condition={isSuperAdmin}
                                        fallback="Check back later for help and training content."
                                    >
                                        Create a category and upload materials
                                        from the Upload page.
                                    </When>
                                </Text>
                            </Container>
                        }
                    >
                        <Tabs
                            color="blue"
                            value={activeCategoryId}
                            onChange={setActiveCategoryId}
                            orientation="vertical"
                            className="flex w-full"
                            classNames={{
                                tab: 'text-gray-400 hover:bg-slate-100 data-[active]:text-[#00539f] data-[active]:font-medium',
                                tabLabel: 'text-left justify-start w-full',
                            }}
                        >
                            <Tabs.List className="sticky top-24 self-start z-20 bg-light-grey max-h-[calc(100vh-6rem)] overflow-y-auto min-w-[14rem]">
                                {categories.map(category => {
                                    const count =
                                        materialsByCategory.get(category.id)
                                            ?.length ?? 0;
                                    return (
                                        <Tabs.Tab
                                            key={category.id}
                                            value={String(category.id)}
                                        >
                                            <Group
                                                justify="space-between"
                                                gap="xs"
                                                wrap="nowrap"
                                                className="w-full"
                                            >
                                                <Text size="sm" lineClamp={1}>
                                                    {category.name}
                                                </Text>
                                                <Badge
                                                    size="sm"
                                                    variant="light"
                                                    color="gray"
                                                >
                                                    {count}
                                                </Badge>
                                            </Group>
                                        </Tabs.Tab>
                                    );
                                })}
                            </Tabs.List>

                            <div className="flex-1 min-w-0 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                {categories.map(category => (
                                    <Tabs.Panel
                                        key={category.id}
                                        value={String(category.id)}
                                    >
                                        <TrainingCategoryMaterialsPanel
                                            category={category}
                                            materials={
                                                materialsByCategory.get(
                                                    category.id,
                                                ) ?? []
                                            }
                                            searchValue={
                                                searchByCategoryId[
                                                    category.id
                                                ] ?? ''
                                            }
                                            isSuperAdmin={isSuperAdmin}
                                            actionLoadingId={actionLoadingId}
                                            onSearchChange={handleCategorySearchChange(
                                                category.id,
                                            )}
                                            onView={handleViewMaterial}
                                            onDownload={handleDownloadMaterial}
                                            onEdit={setEditTarget}
                                            onDelete={setDeleteTarget}
                                        />
                                    </Tabs.Panel>
                                ))}
                            </div>
                        </Tabs>
                    </When>
                </When>
            </div>

            <TrainingMaterialViewer
                opened={Boolean(viewerMaterial)}
                onClose={handleCloseViewer}
                material={viewerMaterial}
                viewUrl={viewUrl}
                loading={viewLoading}
                onDownload={handleDownloadViewerMaterial}
            />

            <TrainingMaterialEditModal
                opened={Boolean(editTarget)}
                onClose={handleCloseEdit}
                material={editTarget}
                categories={categories}
                loading={updating}
                onSave={handleSaveEdit}
            />

            <ConfirmDeleteModal
                opened={Boolean(deleteTarget)}
                close={handleCloseDelete}
                onConfirm={handleDelete}
                loading={deleting}
                subject={deleteTarget?.title}
                message={`Delete "${deleteTarget?.title}"? This cannot be undone.`}
            />
        </div>
    );
}

export default TrainingMaterialsPage;
